import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { taskQueries } from '../api/task';
import { isActiveTaskStatus, matchesTaskTab } from '../constants';
import useTaskApiInstance from './useTaskApiInstance';

import type { Task, TaskListParams, TaskTabKey } from '../constants';

/** 진행 중 task가 있을 때의 폴링 주기(ms). */
export const TASK_POLL_INTERVAL = 3000;

/**
 * 폴링 드라이버와 UI가 동일 query 캐시를 공유하도록 고정하는 기본 list 파라미터.
 * @description
 * - 페이지네이션 없이 전체를 한 번에 받는다 (`limit: -1`).
 * - 이 객체가 곧 queryKey의 일부이므로, 동일 키 공유를 위해 반드시 같은 값을 써야 한다.
 */
export const DEFAULT_TASK_LIST_PARAMS: TaskListParams = {
  limit: -1,
  sorts: 'updatedAt:desc',
};

export interface UseTaskListOptions {
  enabled?: boolean;
  /** 기본 파라미터에 병합할 추가 조회 파라미터. */
  params?: TaskListParams;
}

/**
 * Task 목록 조회 + 자기-조절(self-regulating) 3초 폴링 훅.
 * @description
 * - 진행 중(active) task가 응답에 1개라도 있으면 `refetchInterval`이 3초를 반환해 폴링.
 * - 전부 완료(completed)되면 `false`를 반환해 폴링이 스스로 멈춘다 (별도 플래그 불필요).
 * - 멈춘 폴링은 SSE TASK 이벤트(useTaskListWithSSE)의 invalidate로 강제 refetch될 때 재평가되어 재개된다.
 * - 단일 list를 받아 탭별(전체/진행중/성공/실패) 카운트와 필터 함수를 함께 제공한다.
 */
const useTaskList = (options: UseTaskListOptions = {}) => {
  const { enabled = true, params } = options;
  const { instance, endpoint } = useTaskApiInstance();

  const mergedParams = useMemo<TaskListParams>(
    () => ({ ...DEFAULT_TASK_LIST_PARAMS, ...params }),
    [params],
  );

  const query = useQuery({
    ...taskQueries.list(instance, mergedParams),
    enabled: enabled && Boolean(endpoint),
    refetchInterval: ({ state }) => {
      const tasks = state.data?.data ?? [];
      return tasks.some(task => isActiveTaskStatus(task.statusType)) ? TASK_POLL_INTERVAL : false;
    },
    refetchIntervalInBackground: true,
  });

  const tasks = useMemo<Task[]>(() => query.data?.data ?? [], [query.data]);

  const counts = useMemo(
    () => ({
      all: tasks.length,
      in_progress: tasks.filter(task => matchesTaskTab('in_progress', task.statusType)).length,
      success: tasks.filter(task => matchesTaskTab('success', task.statusType)).length,
      failed: tasks.filter(task => matchesTaskTab('failed', task.statusType)).length,
    }),
    [tasks],
  );

  const filterByTab = useMemo(
    () => (tab: TaskTabKey) => tasks.filter(task => matchesTaskTab(tab, task.statusType)),
    [tasks],
  );

  return {
    ...query,
    tasks,
    counts,
    filterByTab,
    hasActiveTask: counts.in_progress > 0,
  };
};

export default useTaskList;
